import {
    CollectionPreferencesProps,
    PaginationProps,
    PropertyFilterProps,
    TableProps
} from "@amzn/awsui-components-react";
import * as React from "react";
import { ILegalContact, ServerSidePaginatedLoadingOutput } from "@amzn/ask-legal-domain";
import { ComponnentProps, useComponnentProps } from "./polaris-component-hooks";
import { LegalContactsPolarisFactory } from "../factory/polaris/legal-contacts-polaris-factory";
import { API2Interface } from "./api-hook";
import { propertyFilterI18nStrings } from "../i18n/propertyFilteringI18nString";

export interface ServerSideCollection<T> {
    tableProps: ComponnentProps<TableProps<T>>;
    preferenceProps: ComponnentProps<CollectionPreferencesProps>;
    paginationProps: ComponnentProps<PaginationProps>;
    filteringProps: ComponnentProps<PropertyFilterProps>;
    loadItems: () => void;
}

export function useServerSideCollection<T>(
    apiRunner: API2Interface<ILegalContact.LoadAllLegalContactsInput, ServerSidePaginatedLoadingOutput<T>>,
    instanceId: string,
    options: {
        columnDefinitions: TableProps.ColumnDefinition<any>[],
        defaultSortingColumn: TableProps.SortingColumn<T>,
        filteringProperties: PropertyFilterProps.FilteringProperty[],
        selectionType?: "single" | "multi",
        /** Optional filter tokens to seed the query with on first load (e.g. a ?alias= pre-filter). */
        initialFilterTokens?: PropertyFilterProps.Query["tokens"],
        /** Optional predefined filter values (e.g. the relationship enum) shown as a value dropdown. */
        filteringOptions?: PropertyFilterProps.FilteringOption[]
    }
): ServerSideCollection<T> {
    const tableProps = useComponnentProps<TableProps<T>>({
        items: [],
        totalItemsCount: 0,
        columnDefinitions: options.columnDefinitions,
        selectedItems: [],
        selectionType: options.selectionType,
        onSelectionChange: e => tableProps.setProps((prev) => ({
            ...prev,
            selectedItems: e.detail.selectedItems
        })),
        sortingColumn: options.defaultSortingColumn,
        sortingDescending: false,
        onSortingChange: e => tableProps.setProps((prev) => ({
            ...prev,
            sortingColumn: { sortingField: e.detail.sortingColumn.sortingField },
            sortingDescending: e.detail.isDescending
        })),
        loadingText: "Loading...",
        loading: true
    });

    const paginationProps = useComponnentProps<PaginationProps>({
        currentPageIndex: 1,
        pagesCount: 0,
        onChange: (e) => {
            paginationProps.setProps((prev) => ({
                ...prev,
                currentPageIndex: e.detail.currentPageIndex
            }));
        }
    });

    const preferenceProps = useComponnentProps<CollectionPreferencesProps>({
        cancelLabel: "Cancel",
        confirmLabel: "Confirm",
        title: "Table Preferences",
        preferences: LegalContactsPolarisFactory.DEFAULT_PREFERENCES,
        pageSizePreference: {
            title: "Select page size",
            options: LegalContactsPolarisFactory.DEFAULT_PAGE_SIZE_OPTIONS
        },
        onConfirm: (e) => {
            preferenceProps.setProps((prev) => ({
                ...prev,
                preferences: e.detail
            }));
        }
    });

    // Single source of truth for the query. PropertyFilter is controlled, so onChange writes the
    // new query straight into the prop it renders from — no separate "query state" mirrored in via
    // a lagging effect. The old double-buffer meant a filter change didn't reach the controlled
    // query (or the re-fetch) until a second render, so the first apply silently no-op'd and you
    // had to re-trigger it. Writing directly makes the change take effect on the first apply.
    const filteringProps = useComponnentProps<PropertyFilterProps>({
        query: { operation: "or", tokens: options.initialFilterTokens || [] },
        onChange: e => filteringProps.setProps(prev => ({
            ...prev,
            query: {
                // With hideOperations + a single token, Polaris can emit an undefined operation.
                // Keep the last known operation (defaults to "or") so the query never loses it —
                // otherwise getLegalContactFilters drops the whole filter and shows everyone.
                operation: e.detail.operation || prev.query.operation || "or",
                tokens: e.detail.tokens.map(t => ({
                    ...t,
                    propertyKey: t.propertyKey
                }))
            }
        })),
        filteringProperties: options.filteringProperties,
        filteringOptions: options.filteringOptions || [],
        i18nStrings: propertyFilterI18nStrings,
        hideOperations: true,
    });

    const getLegalContactFilters = () => {
        // Only the token list gates whether we filter. Operation must NOT gate it: a falsy
        // operation (Polaris omits it for a single token) previously dropped the entire filter,
        // making manually-added chips silently no-op ("shows everyone"). Default to "Or".
        if (filteringProps.props.query.tokens.length === 0) return undefined;
        return {
            operation: filteringProps.props.query.operation === "and" ? "And" : "Or",
            propertyTokens: filteringProps.props.query.tokens.map((token) => ({
                name: token.propertyKey || LegalContactsPolarisFactory.Filters.PropertyFilters.LegalContactUserName.key,
                value: token.value,
                operator: token.operator || ":"
            }))
        };
    };

    const loadItems = () => {
        apiRunner.invoke(
            ILegalContact.LoadAllLegalContactsInput.create({
                instanceId: instanceId,
                pagination: {
                    currentPageIndex: paginationProps.props.currentPageIndex,
                    pageSize: preferenceProps.props.preferences.pageSize,
                },
                sorting: {
                    field: tableProps.props.sortingColumn.sortingField,
                    order: tableProps.props.sortingDescending ? "DESC" : "ASC"
                },
                filters: getLegalContactFilters()
            })
        );
    };

    React.useEffect(() => {
        loadItems();
    }, [
        instanceId,
        tableProps.props.sortingColumn,
        tableProps.props.sortingDescending,
        paginationProps.props.currentPageIndex,
        filteringProps.props.query
    ]);

    React.useEffect(() => {
        if (apiRunner.status === "Succeeded") {
            tableProps.setProps(prev => ({
                ...prev,
                loading: false,
                items: apiRunner.output.result,
                totalItemsCount: apiRunner.output.totalCount
            }));
            paginationProps.setProps(prev => ({
                ...prev,
                pagesCount: Math.floor(
                    (apiRunner.output.totalCount + preferenceProps.props.preferences.pageSize - 1) / preferenceProps.props.preferences.pageSize
                ),
                disabled: false
            }));
            preferenceProps.setProps(prev => ({
                ...prev,
                disabled: false
            }));
        } else if (apiRunner.status === "Running") {
            tableProps.setProps((prev) => ({
                ...prev,
                loading: true
            }));
            paginationProps.setProps((prev) => ({
                ...prev,
                disabled: true
            }));
            preferenceProps.setProps((prev) => ({
                ...prev,
                disabled: true
            }));
        } else if (apiRunner.status === "Error") {
            tableProps.setError(apiRunner.err);
            tableProps.setProps((prev) => ({
                ...prev,
                loading: false,
                selectedItems: []
            }));
            paginationProps.setProps((prev) => ({
                ...prev,
                disabled: false
            }));
            preferenceProps.setProps((prev) => ({
                ...prev,
                disabled: false
            }));
        }
    }, [apiRunner.status]);

    React.useEffect(() => {
        tableProps.setProps(prev => ({
            ...prev,
            visibleColumns: preferenceProps.props.preferences.visibleContent
        }));
    }, [preferenceProps.props.preferences.visibleContent]);


    // When page size changes, change current page index back to 1
    React.useEffect(() => {
        if (tableProps.props.loading) return;
        if (paginationProps.props.currentPageIndex !== 1) {
            paginationProps.setProps(prev => ({
                ...prev,
                currentPageIndex: 1
            }));
        } else {
            loadItems();
        }
    }, [preferenceProps.props.preferences.pageSize]);

    return {
        tableProps,
        preferenceProps,
        paginationProps,
        filteringProps,
        loadItems
    };
}